import * as React from "react";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@/store/userStore";
import { useSettingsStore } from "@/store/api/settingsStore";
import { fr, enUS } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BookingDetailsTab } from "./booking-dialog/BookingDetailsTab";
import { BookingAttendeesTab } from "./booking-dialog/BookingAttendeesTab";
import { ClassBookingDialogProps } from "./booking-dialog/types";
import { useClassBookingWithStore } from "./booking-dialog/useClassBookingWithStore";
import { useEffect, useState } from "react";
import { Clock, Loader2, Pencil, Trash2, AlertCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useScheduleClasses } from "@/hooks/useScheduleClasses";
import { useClassData } from "./booking-dialog/hooks/useClassData";
import { BookingConfirmationDialog } from "./booking-dialog/BookingConfirmationDialog";
import { resolveCalendarEntitlement } from "@/lib/subscriptionCalendars";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from "@/components/ui/alert-dialog";

export const ClassBookingDialog = ({
  isOpen,
  onOpenChange,
  classId,
  date,
  onConfirm,
  isScheduleView = false,
  clientSubscriptionEndDate,
  clientRemainingSessions,
  clientCoursesBooked,
  clientActiveSubscriptions,
  clientUpcomingByCalendar,
  clientDataUnavailable,
  onRetryClientData,
  onCapacityUpdate,
  handleDeleteClass,
  classes = [],
  addRecentlyBookedSlot,
  onEditClass
}: ClassBookingDialogProps) => {

  const { t, i18n } = useTranslation(["classes", "common", "schedule", "bookingui"]);
  const { role } = useUserStore();
  const { settings } = useSettingsStore();
  const isAdmin = role === 'admin';
  const isCoach = role === 'coach';
  const [loading, setLoading] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const isMobile = useIsMobile();
  
  // Get booking advance days from settings
  const bookingAdvanceDays = settings?.schedule?.bookingAdvanceDays || 0;

  const [activeTab, setActiveTab] = useState<string>(isAdmin || isCoach ? "attendees" : "details");

  const {
    selectedClass,
    selectedDate,
    selectedTime,
    handleTimeChange,
    availableSlots,
    handleSlotsChange,
    totalCapacity,
    searchQuery,
    setSearchQuery,
    filteredAttendees,
    allAttendees,
    handleAttendeeToggle,
    confirmBooking,
    refreshClassData,
    isLoadingClients,
    initialSelectedAttendees,
    isFullyLoaded,
    loadError,
    retryLoad,
  } = useClassBookingWithStore(isOpen, classId, date, onOpenChange, onConfirm, classes, addRecentlyBookedSlot);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isRetryingClientData, setIsRetryingClientData] = useState(false);

  // Active tab selon le contexte - seulement à l'ouverture du dialog
  useEffect(() => {
    if (isOpen) {
      setActiveTab(isAdmin || isCoach ? "attendees" : "details");
    }
  }, [isOpen, isAdmin, isCoach]);

  if (!selectedClass) return null;

  // For admin/coach flow we route through the impact confirmation dialog.
  // For athletes (self-booking), we keep the direct flow.
  const handleConfirmClick = async () => {
    // Capacity-only change (admin) — apply immediately, no booking impact dialog needed
    const capacityChanged = (isAdmin || isCoach) && onCapacityUpdate && totalCapacity !== selectedClass.capacity;
    if (capacityChanged) {
      try {
        await onCapacityUpdate!(classId, totalCapacity);
      } catch (e) {
        console.error("[ClassBookingDialog] capacity update error", e);
      }
    }

    if (isAdmin || isCoach) {
      // Compute participant diff: only show the impact dialog if attendees actually changed.
      const selectedNowIds = new Set(allAttendees.filter(a => a.selected).map(a => a.id));
      let added = 0;
      let removed = 0;
      for (const id of selectedNowIds) {
        if (!initialSelectedAttendees.has(id)) added++;
      }
      for (const id of initialSelectedAttendees) {
        if (!selectedNowIds.has(id)) removed++;
      }

      if (added === 0 && removed === 0) {
        // Pas de changement participants : si la capacité a bougé, on rafraîchit la vue planning.
        if (capacityChanged) {
          try {
            await refreshClassData();
          } catch (e) {
            console.error("[ClassBookingDialog] refresh after capacity update failed", e);
          }
        }
        onOpenChange(false);
        return;
      }

      // Participant change: open impact dialog; actual mutation happens in its onConfirm.
      setConfirmOpen(true);
      return;
    }


    // Athlete: direct flow
    setLoading(true);
    try {
      // On ne referme le dialogue que si rien n'a échoué : sinon l'utilisateur
      // voyait le dialogue se fermer comme après un succès alors que le toast
      // d'erreur annonçait le contraire.
      const outcome = await confirmBooking();
      if (outcome.failed === 0) {
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Error during confirmation:", error);
    } finally {
      setLoading(false);
    }
  };

  const runAdminConfirm = async () => {
    try {
      const outcome = await confirmBooking();
      // Idem côté admin : en cas d'échec partiel, on garde le dialogue ouvert
      // pour que l'admin voie qui n'a pas été inscrit et puisse réessayer.
      if (outcome.failed === 0) {
        onOpenChange(false);
      }
    } catch (error) {
      console.error("Error during admin confirmation:", error);
      throw error;
    }
  };

  // --- Suppression du cours ---
  const deleteClass = async () => {
    if (!classId) return;
    setDeleting(true);
    try {
      // ⚠️ change "classes" si ta table a un autre nom
      await handleDeleteClass(classId);
    
      await refreshClassData();
      
      onOpenChange(false);
    } catch (e) {
      console.error("Unexpected delete error:", e);
    } finally {
      setDeleting(false);
    }
  };

  const handleRetryClientData = async () => {
    if (!onRetryClientData) return;
    setIsRetryingClientData(true);
    try {
      await onRetryClientData();
    } catch (e) {
      console.error("[ClassBookingDialog] retry client data failed", e);
    } finally {
      setIsRetryingClientData(false);
    }
  };

  // Vérification d’abonnement (côté élève)
  //
  // ⚠️ « quota inconnu » ≠ « quota épuisé ». Quand la lecture de l'abonnement
  // a échoué (réseau, refus serveur…), on n'a pas le droit d'en déduire qu'il
  // reste 0 séance : on affiche un message d'erreur distinct avec réessai et on
  // laisse le serveur trancher (il refuse avec NO_SESSIONS_AVAILABLE si le
  // quota est réellement épuisé).
  //
  // ⚠️ Le quota est CONTEXTUEL AU PLANNING DU COURS. Un adhérent peut détenir
  // plusieurs abonnements actifs de plannings différents, aux quotas et aux
  // dates de fin indépendants : ce sont ceux de l'abonnement (ou des
  // abonnements) couvrant le calendrier de CE cours qui s'appliquent. Le
  // comptage des réservations à venir est ventilé de la même façon — comparer
  // le total toutes disciplines confondues au quota d'un seul planning ferait
  // refuser à tort (six réservations de Pilates épuisant le quota « Salle »).
  //
  // Salle sans calendrier / abonnement unique : l'abonnement est universel, il
  // couvre le cours, et l'entitlement reproduit champ pour champ les valeurs
  // d'avant.
  const entitlement = resolveCalendarEntitlement({
    subscriptions: clientActiveSubscriptions,
    calendarId: selectedClass?.calendar_id ?? null,
    upcoming: { total: clientCoursesBooked ?? null, byCalendar: clientUpcomingByCalendar ?? null },
    legacy: {
      dataUnavailable: clientDataUnavailable,
      sessionsRemaining: clientRemainingSessions,
      endDate: clientSubscriptionEndDate,
    },
  });

  const isSubscriptionStatusUnknown = entitlement.status === 'unknown';
  const sessionsRemaining = entitlement.sessionsRemaining ?? 0;
  const upcomingClassesCount = entitlement.upcomingCount ?? 0;
  const endDate = entitlement.endDate ? new Date(entitlement.endDate) : null;
  const classDate = selectedDate ? new Date(selectedDate) : null;

  const isAfterEndDate = !isSubscriptionStatusUnknown && endDate && classDate && classDate > endDate;
  const isOutOfSessions =
    !isSubscriptionStatusUnknown &&
    (sessionsRemaining <= 0 || sessionsRemaining <= upcomingClassesCount);
  // Aucun abonnement actif ne couvre ce planning : ce n'est pas « plus de
  // séances », c'est « pas le bon abonnement ». Le message doit le dire.
  const isCalendarNotCovered = !isSubscriptionStatusUnknown && !entitlement.isCalendarCovered;

  // Check booking advance days limit
  const isDateBeyondBookingLimit = (() => {
    if (!bookingAdvanceDays || bookingAdvanceDays === 0) return false; // 0 = unlimited
    if (!classDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + bookingAdvanceDays);
    maxDate.setHours(23, 59, 59, 999);
    return classDate > maxDate;
  })();

  if (!isAdmin && !isCoach && (isAfterEndDate || isOutOfSessions || isDateBeyondBookingLimit || isCalendarNotCovered)) {
    let message = isDateBeyondBookingLimit
      ? t('schedule.bookingAdvanceLimitReached', {
          days: bookingAdvanceDays,
          defaultValue: `Vous ne pouvez pas réserver plus de ${bookingAdvanceDays} jours à l'avance.`
        })
      : isCalendarNotCovered
        ? t('booking.calendarNotCovered', {
            ns: 'classes',
            defaultValue: "Ce cours appartient à un planning qui n'est pas inclus dans votre abonnement."
          })
      : isAfterEndDate
        ? t('schedule.bookingLimitReached', {
            defaultValue: "Ce cours se déroule après la date d'expiration de votre abonnement. Veuillez le renouveler."
          })
        : t('schedule.bookingLimitReached', {
            defaultValue: "Vous avez atteint la limite de séances autorisées par votre abonnement. Veuillez le renouveler."
          });

    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('booking.title', { ns: 'classes' })}</DialogTitle>
          </DialogHeader>
          <div className="pb-4 text-center text-sm text-red-600 border border-red-300 rounded-md p-4 bg-red-50">
            {message}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('close', { ns: 'common', defaultValue: 'Fermer' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const dateLocale = i18n.language === 'fr' ? fr : enUS;
  const classDuration = selectedClass.duration || '60';

  const dialogTitle = isAdmin 
    ? (isScheduleView 
        ? t('addAthlete', { ns: 'schedule' })
        : t('booking.adminTitle', { ns: 'classes' }))
    : t('booking.title', { ns: 'classes' });

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg overflow-hidden">
        <DialogHeader className="min-w-0">
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription asChild>
            <div className="min-w-0">
              {isAdmin 
                ? t('booking.adminDescription', { ns: 'classes' })
                : t('booking.description', { ns: 'classes' })} {selectedClass.name}
              {classDuration && (
                <div className="flex items-center mt-1 text-sm text-muted-foreground">
                  <Clock className="h-3 w-3 mr-1" />
                  <span>{classDuration} {t('minutes', { ns: 'classes' })}</span>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Quota inconnu : on informe sans bloquer. La réservation reste
            possible, c'est le serveur qui refusera si le quota est épuisé. */}
        {!isAdmin && !isCoach && isSubscriptionStatusUnknown && (
          <div
            role="status"
            className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 sm:flex-row sm:items-start sm:justify-between"
          >
            <div className="flex items-start gap-2 min-w-0">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="min-w-0">
                {t('booking.subscriptionStatusUnavailable', {
                  ns: 'classes',
                  defaultValue:
                    "Impossible de vérifier votre abonnement pour le moment. Vous pouvez tenter la réservation : elle sera confirmée ou refusée par le serveur."
                })}
              </span>
            </div>
            {onRetryClientData && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 self-start"
                onClick={handleRetryClientData}
                disabled={isRetryingClientData}
              >
                {isRetryingClientData && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                {t('retry', { ns: 'common', defaultValue: 'Réessayer' })}
              </Button>
            )}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="min-w-0 overflow-hidden">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">{t('details', { ns: 'common' })}</TabsTrigger>
            {(isAdmin || isCoach) && (
              <TabsTrigger value="attendees" className="relative">
                {t('attendees', { ns: 'schedule' })}
                {filteredAttendees.some(a => a.selected) && (
                  <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full w-4 h-4 text-xs flex items-center justify-center">
                    {filteredAttendees.filter(a => a.selected).length}
                  </span>
                )}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="details" className="min-w-0 overflow-hidden">
            <BookingDetailsTab
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              handleTimeChange={handleTimeChange}
              availableSlots={availableSlots}
              handleSlotsChange={handleSlotsChange}
              dateLocale={dateLocale}
              totalCapacity={totalCapacity}
              duration={classDuration}
            />
          </TabsContent>

          {(isAdmin || isCoach) && (
            <TabsContent value="attendees" className="min-w-0 overflow-hidden">
              <BookingAttendeesTab
                isCoach={isCoach}
                availableSlots={availableSlots}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                filteredAttendees={filteredAttendees}
                handleAttendeeToggle={handleAttendeeToggle}
                isLoadingClients={isLoadingClients}
                onTrialUpdated={retryLoad}
              />
            </TabsContent>
          )}
        </Tabs>

        <DialogFooter className="gap-2 sm:gap-0 justify-between">
          {(isAdmin && activeTab==="details") && (
            <div className="flex gap-2 order-1 sm:order-none">
              {isScheduleView && onEditClass && (
                <Button
                  type="button"
                  variant="outline"
                  size={isMobile ? "sm" : "default"}
                  onClick={() => {
                    onOpenChange(false);
                    onEditClass();
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  {t('common:edit', { defaultValue: 'Modifier' })}
                </Button>
              )}
              <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="destructive"
                  size={isMobile ? "sm" : "default"}
                  disabled={deleting}
                  className="order-1 sm:order-none"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common:loading', { defaultValue: 'Chargement...' })}
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('common:delete', { defaultValue: 'Supprimer' })}
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('classes:deleteTitle', { defaultValue: "Supprimer ce cours ?" })}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('classes:deleteDesc', { defaultValue: "Cette action est irréversible. Les réservations associées pourront être affectées." })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>
                    {t('common:cancel', { defaultValue: 'Annuler' })}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={deleteClass}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    {t('common:confirm', { defaultValue: 'Confirmer' })}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            </div>
          )}

          {/* Boutons annuler / confirmer */}
          <div className="flex flex-col gap-2 ml-auto w-full sm:w-auto sm:flex-row sm:items-center">
            {(isAdmin || isCoach) && loadError && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded px-2 py-1">
                <AlertCircle className="h-3 w-3 shrink-0" />
                <span>{t("classBookingDialog.incompleteLoading", { ns: "bookingui" })}</span>
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={retryLoad}
                >
                  {t("classBookingDialog.retry", { ns: "bookingui" })}
                </button>
              </div>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button
                onClick={handleConfirmClick}
                disabled={
                  loading ||
                  ((isAdmin || isCoach) && (!isFullyLoaded || !!loadError))
                }
              >
                {((isAdmin || isCoach) && !isFullyLoaded && !loadError) ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    {t("classBookingDialog.loadingAttendees", { ns: "bookingui" })}
                  </>
                ) : loading ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    {t('loading', { ns: 'common', defaultValue: 'Loading...' })}
                  </>
                ) : (
                  t('confirm', { ns: 'common' })
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {(isAdmin || isCoach) && (
        <BookingConfirmationDialog
          isOpen={confirmOpen}
          onOpenChange={setConfirmOpen}
          attendees={allAttendees}
          initialSelectedIds={initialSelectedAttendees}
          totalCapacity={totalCapacity}
          onConfirm={runAdminConfirm}
        />
      )}
    </Dialog>
  );
};
